import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Box, Button, CircularProgress, Divider, TextField, Typography } from '@mui/material';
import { Formik } from 'formik';

import localization from '../../localization';
import {
  loginUser,
  loginWithGoogle,
  setIncludesDefault,
  setNexwayState,
} from '../../redux/actions/Account';
import auth from '../../services/auth';
import parentPaths from '../../services/paths';
import TwoFALoginPopup from '../Popup/2FALoginPopup/TwoFALoginPopup';

const initialValues = { username: '', password: '' };

const LoginForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [token, setToken] = useState('');
  const [googleConfig, setGoogleConfig] = useState(null);

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const { state: { referrer } = {} } = location;
  const realm = parentPaths.config.currentRealm;

  // Initialize Google SSO
  useEffect(() => {
    const initGoogleSSO = async () => {
      try {
        const config = await auth.createGoogleSession(realm);

        if (config && config.clientId) {
          setGoogleConfig(config);
        }
      } catch (error) {
        console.error('Failed to initialize Google SSO:', error);
      }
    };

    initGoogleSSO();
  }, [realm]);

  // Handle Google credential response
  const handleGoogleCredentialResponse = async (response) => {
    const idToken = response.credential;

    dispatch(
      loginWithGoogle({
        credentials: {
          idToken,
          realm,
        },
        onSuccess: redirectOnLogin,
      }),
    );
  };

  // Initialize Google Sign-In button when config is available
  useEffect(() => {
    if (googleConfig && window.google) {
      window.google.accounts.id.initialize({
        client_id: googleConfig.clientId,
        callback: handleGoogleCredentialResponse,
        nonce: googleConfig.nonce,
      });

      window.google.accounts.id.renderButton(document.getElementById('google-signin-button'), {
        type: 'standard',
        theme: 'outline',
        text: 'signin_with',
        size: 'large',
        width: '100%',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleConfig]);

  const redirectOnLogin =
    referrer &&
    (() => {
      history.push(referrer);
    });

  const afterLogin = () => {
    const localNxState = JSON.parse(localStorage.getItem('nexwayState'));
    const localIncludesDefault = JSON.parse(localStorage.getItem('includesDefault'));

    dispatch(setNexwayState(localNxState));
    dispatch(setIncludesDefault(localIncludesDefault));
  };

  const onTwoFARequest = (accessToken) => {
    setIsOpen(true);
    setToken(accessToken);
  };

  const onSuccessTwoFA = () => {
    setIsOpen(false);
    redirectOnLogin?.();
    afterLogin();
  };

  const handleOnSubmit = async ({ username, password, setSubmitting }) => {
    setSubmitting(true);

    const restSubmitting = () => setSubmitting(false);

    dispatch(
      loginUser({
        credentials: {
          username,
          password,
          realm,
        },
        onSuccess: redirectOnLogin,
        onError: restSubmitting,
        onTwoFA: onTwoFARequest,
      }),
    );
    afterLogin();
  };

  return (
    <>
      <Formik
        className='formik'
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => handleOnSubmit({ ...values, setSubmitting })}
      >
        {({ errors, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              error={touched.username && errors.username}
              fullWidth
              helperText={touched.username ? errors.username : ''}
              label={localization.t('forms.inputs.username')}
              margin='normal'
              name='username'
              autoComplete='username'
              onChange={handleChange}
              type='text'
              value={values.username}
              variant='outlined'
              inputProps={{ style: { WebkitBoxShadow: '0 0 0 1000px white inset' } }}
            />

            <TextField
              error={touched.password && errors.password}
              fullWidth
              helperText={touched.password ? errors.password : ''}
              label={localization.t('forms.inputs.password')}
              margin='normal'
              name='password'
              autoComplete='current-password'
              onChange={handleChange}
              type='password'
              value={values.password}
              variant='outlined'
              inputProps={{ style: { WebkitBoxShadow: '0 0 0 1000px white inset' } }}
            />

            <Box mt={2}>
              <Button
                className='loginButton'
                color='loginBtns'
                disabled={
                  Object.keys(errors).length !== 0 ||
                  !values.username ||
                  !values.password ||
                  isSubmitting
                }
                fullWidth
                size='large'
                type='submit'
                variant='contained'
                style={{ borderRadius: 0, color: '#ffffff' }}
              >
                {isSubmitting ? (
                  <CircularProgress size={26} />
                ) : (
                  localization.t('forms.buttons.signIn')
                )}
              </Button>
            </Box>

            {googleConfig && (
              <>
                <Divider sx={{ my: 3 }}>
                  <Typography variant='body2' color='text.secondary'>
                    OR
                  </Typography>
                </Divider>

                <Box id='google-signin-button' height='45px' width='100%' />
              </>
            )}
          </form>
        )}
      </Formik>
      <TwoFALoginPopup
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSuccess={onSuccessTwoFA}
        token={token}
        realm={realm}
      />
    </>
  );
};

export default LoginForm;
